/**
 * AI Chat — floating assistant widget
 *
 * UI shell only — toggle, textarea auto-resize, chip presets.
 * Wire up your own WebSocket / API transport by calling AiChat.onSend(fn).
 *
 * Usage:
 *   <script src="{% static 'js/ai-chat.js' %}"></script>
 *   <script>
 *     AiChat.init();                       // renders FAB + panel into <body>
 *     AiChat.onSend(function(text) {       // hook up your backend
 *       ws.send(JSON.stringify({ message: text }));
 *     });
 *   </script>
 *
 * Public API:
 *   AiChat.init(opts)          — render and bind (call once)
 *   AiChat.open() / .close() / .toggle()
 *   AiChat.onSend(fn)         — register send handler: fn(text)
 *   AiChat.addMessage(role, html)  — append a bubble ('user' | 'ai' | 'system')
 *   AiChat.setWorking(bool)   — show/hide thinking indicator
 *   AiChat.setChips(arr)      — replace quick-action chips
 */
var AiChat = (function () {
  'use strict';

  var isOpen = false;
  var sendHandler = null;
  var els = {};

  // ── Default chips (override via init opts or setChips) ──
  var defaultChips = [
    { icon: 'fa-solid fa-circle-info', label: 'Status',      message: 'What is the current status?' },
    { icon: 'fa-solid fa-list-check',  label: 'Summary',     message: 'Give me a summary.' },
    { icon: 'fa-solid fa-lightbulb',   label: 'Suggestions', message: 'Any suggestions?' }
  ];

  // ── Render ──
  function render(opts) {
    opts = opts || {};
    var chips = opts.chips || defaultChips;
    var title = opts.title || 'AI Assistant';
    var icon  = opts.icon  || 'fa-solid fa-wand-magic-sparkles';
    var placeholder = opts.placeholder || 'Type a message\u2026';

    // FAB
    var fab = document.createElement('button');
    fab.className = 'ai-chat-fab';
    fab.setAttribute('aria-label', 'Open AI assistant');
    fab.innerHTML = '<i class="' + icon + '"></i>';
    document.body.appendChild(fab);
    els.fab = fab;

    // Panel
    var panel = document.createElement('div');
    panel.className = 'ai-chat';
    panel.id = 'ai-chat-panel';
    panel.innerHTML =
      '<div class="ai-chat__header">' +
        '<span class="ai-chat__title"><i class="' + icon + '"></i> ' + title + '</span>' +
        '<button class="ai-chat__close" aria-label="Close"><i class="fa-solid fa-xmark"></i></button>' +
      '</div>' +
      '<div class="ai-chat__messages" id="ai-chat-messages">' +
        '<div class="ai-chat__empty"><i class="' + icon + '"></i>Ask me anything</div>' +
      '</div>' +
      '<div class="ai-chat__chips" id="ai-chat-chips"></div>' +
      '<form class="ai-chat__input-row" id="ai-chat-form">' +
        '<textarea class="ai-chat__input" id="ai-chat-input" placeholder="' + placeholder + '" rows="1" data-autoresize></textarea>' +
        '<button type="submit" class="ai-chat__send" aria-label="Send"><i class="fa-solid fa-paper-plane"></i></button>' +
      '</form>';
    document.body.appendChild(panel);
    els.panel    = panel;
    els.messages = panel.querySelector('#ai-chat-messages');
    els.chips    = panel.querySelector('#ai-chat-chips');
    els.form     = panel.querySelector('#ai-chat-form');
    els.input    = panel.querySelector('#ai-chat-input');
    els.send     = panel.querySelector('.ai-chat__send');
    els.close    = panel.querySelector('.ai-chat__close');
    els.empty    = panel.querySelector('.ai-chat__empty');

    renderChips(chips);
    bind();
  }

  function renderChips(chips) {
    if (!els.chips) return;
    els.chips.innerHTML = '';
    chips.forEach(function (c) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'chip chip-sm chip-muted';
      if (c.message) btn.setAttribute('data-message', c.message);
      if (c.preset)  btn.setAttribute('data-preset', c.preset);
      btn.innerHTML =
        (c.icon ? '<i class="chip-icon ' + c.icon + '"></i> ' : '') +
        '<span class="chip-label">' + c.label + '</span>';
      els.chips.appendChild(btn);
    });
  }

  // ── Bind events ──
  function bind() {
    els.fab.addEventListener('click', open);
    els.close.addEventListener('click', close);

    // Send on submit
    els.form.addEventListener('submit', function (e) {
      e.preventDefault();
      send();
    });

    // Enter to send (Shift+Enter for newline)
    els.input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        send();
      }
    });

    // Auto-resize textarea
    els.input.addEventListener('input', autoResize);

    // Chip clicks (delegated)
    els.chips.addEventListener('click', function (e) {
      var chip = e.target.closest('[data-message], [data-preset]');
      if (!chip || chip.disabled) return;

      var msg = chip.getAttribute('data-message');
      if (msg) {
        els.input.value = msg;
        send();
        return;
      }

      var preset = chip.getAttribute('data-preset');
      if (preset) {
        els.input.value = preset;
        els.input.focus();
        autoResize();
      }
    });

    // Escape to close
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && isOpen) close();
    });
  }

  // ── Toggle ──
  function open() {
    isOpen = true;
    els.panel.classList.add('ai-chat--open');
    els.fab.classList.add('hidden');
    els.input.focus();
  }

  function close() {
    isOpen = false;
    els.panel.classList.remove('ai-chat--open');
    els.fab.classList.remove('hidden');
  }

  function toggle() {
    isOpen ? close() : open();
  }

  // ── Send ──
  var sending = false;
  function send() {
    if (sending) return;
    var text = els.input.value.trim();
    if (!text) return;

    sending = true;
    addMessage('user', escapeHtml(text));
    els.input.value = '';
    autoResize();

    if (sendHandler) sendHandler(text);
    // Release on next tick so rapid-fire events (keydown + submit) are absorbed
    setTimeout(function() { sending = false; }, 0);
  }

  // ── Messages ──
  function addMessage(role, html) {
    // Clear empty state
    if (els.empty) {
      els.empty.remove();
      els.empty = null;
    }

    var avatarIcon = role === 'user'
      ? 'fa-solid fa-user'
      : role === 'ai'
        ? 'fa-solid fa-wand-magic-sparkles'
        : '';

    var div = document.createElement('div');
    div.className = 'ai-chat__msg ai-chat__msg--' + role;

    if (role !== 'system') {
      div.innerHTML =
        '<div class="ai-chat__msg-avatar"><i class="' + avatarIcon + '"></i></div>' +
        '<div class="ai-chat__msg-body">' + html + '</div>';
    } else {
      div.innerHTML = '<div class="ai-chat__msg-body">' + html + '</div>';
    }

    els.messages.appendChild(div);
    els.messages.scrollTop = els.messages.scrollHeight;
    return div;
  }

  // ── Working indicator ──
  function setWorking(on) {
    var existing = els.messages.querySelector('.ai-chat__working');
    if (on && !existing) {
      var w = document.createElement('div');
      w.className = 'ai-chat__working';
      w.innerHTML = '<div class="ai-chat__dots"><span></span><span></span><span></span></div> Thinking\u2026';
      els.messages.appendChild(w);
      els.messages.scrollTop = els.messages.scrollHeight;
    } else if (!on && existing) {
      existing.remove();
    }

    // Disable/enable input
    els.input.disabled = on;
    els.send.disabled = on;
    els.input.placeholder = on ? 'Thinking\u2026' : 'Type a message\u2026';
    var chips = els.chips.querySelectorAll('.chip');
    for (var i = 0; i < chips.length; i++) chips[i].disabled = on;
  }

  // ── Helpers ──
  function autoResize() {
    var el = els.input;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 120) + 'px';
  }

  function escapeHtml(str) {
    var d = document.createElement('div');
    d.textContent = str;
    return d.innerHTML;
  }

  // ── Public API ──
  return {
    init: function (opts) { render(opts); },
    open: open,
    close: close,
    toggle: toggle,
    onSend: function (fn) { sendHandler = fn; },
    addMessage: addMessage,
    setWorking: setWorking,
    setChips: renderChips
  };
})();
window.AiChat = AiChat;
